import { DecimalPipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, linkedSignal, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DailyResultsService } from '@services/daily-results';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { firstValueFrom, timer } from 'rxjs';

import { badRequestReason, refusalReason } from '@core/models/apis';
import {
  dailyResultBand,
  DailyResultDay,
  dailyResultSeverity,
  DailyResultsQueueState,
  DailyResultsRescoreRange,
  DailyResultV1,
  MAX_RESCORE_RANGE_DAYS,
  utcStamp
} from '@core/models/daily-results';
import { daysEnding, toIsoDate } from '@core/utils/local-date';

import { DailyResultDetail } from './daily-result-detail/daily-result-detail';

interface SimulationRow {
  key: number;
  nutrientId: string;
  quantity: number | null;
}

/**
 * Where a rescore stands. Marking only queues the days, so the panel follows the queue until the sweep has
 * taken them: `waiting` while it does, `scored` once none remain, `queued` when some still remain at the
 * deadline, and `unknown` when the queue could not say.
 */
type RescoreProgress = 'waiting' | 'scored' | 'queued' | 'unknown';

interface RescoreOutcome {
  marked: number;
  range: DailyResultsRescoreRange | null;
  progress: RescoreProgress;
  queuedDays: number;
  failureReason: string | null;
}

export const LIST_DAYS = 7;
export const SWEEP_POLL_MS = 2_000;
export const SWEEP_TIMEOUT_MS = 60_000;

const MS_PER_DAY = 86_400_000;
const NUTRIENT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let nextRowKey = 0;
const emptyRow = (): SimulationRow => ({ key: nextRowKey++, nutrientId: '', quantity: null });

function daysAgo(days: number): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return date;
}

// Rounded because a range that crosses a daylight-saving change is an hour off a whole number of days.
const inclusiveDays = (from: Date, to: Date): number => Math.round((to.getTime() - from.getTime()) / MS_PER_DAY) + 1;

/**
 * The daily results panel on the Youtopian view: the last seven local days with each stored result and its
 * category and nutrient breakdown, a forced rescore of the look-back window or a date range that reloads the
 * list once the sweep has scored it, and a simulator that scores a supplied day against the member's current
 * targets without storing anything.
 */
@Component({
  selector: 'app-daily-results-panel',
  imports: [
    DecimalPipe,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    MessageModule,
    ProgressSpinnerModule,
    TableModule,
    TagModule,
    DailyResultDetail
  ],
  templateUrl: './daily-results-panel.html',
  styleUrl: './daily-results-panel.scss'
})
export class DailyResultsPanel {
  private readonly dailyResults = inject(DailyResultsService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  private destroyed = false;

  readonly youtopianId = input.required<string>();

  // The picker emits null while its text is cleared or only partly typed.
  readonly through = signal<Date | null>(daysAgo(0));
  readonly latestDay = daysAgo(0);
  readonly listDays = LIST_DAYS;
  readonly maxRescoreRangeDays = MAX_RESCORE_RANGE_DAYS;

  readonly daysResource = this.dailyResults.createDaysResource(() => {
    const through = this.through();
    return through ? { youtopianId: this.youtopianId(), dates: daysEnding(through, LIST_DAYS) } : null;
  });

  // Both empty is the look-back window, which only the engine can reckon: it is a span of local days in each of
  // the Youtopian's households, and this browser's calendar day is a different day from either side of midnight.
  readonly rescoreFrom = signal<Date | null>(null);
  readonly rescoreTo = signal<Date | null>(null);
  readonly rescoring = signal(false);
  readonly simulating = signal(false);

  // The page is reused when the operator follows a link to another Youtopian, so everything that describes one
  // Youtopian starts over when the id changes.
  readonly lastRescore = linkedSignal<string, RescoreOutcome | null>({
    source: this.youtopianId,
    computation: () => null
  });
  readonly simulationRows = linkedSignal<string, SimulationRow[]>({
    source: this.youtopianId,
    computation: () => [emptyRow()]
  });
  readonly simulation = linkedSignal<string, DailyResultV1 | null>({
    source: this.youtopianId,
    computation: () => null
  });
  readonly simulationError = linkedSignal<string, string | null>({
    source: this.youtopianId,
    computation: () => null
  });

  // Held here rather than in the table, which is rebuilt whenever the list is read for another week, so a day
  // the operator opened stays open across a reload. The table writes the keys into this object.
  readonly expandedDays = linkedSignal<string, Record<string, boolean>>({
    source: this.youtopianId,
    computation: () => ({})
  });

  readonly canSimulate = computed(() => {
    const rows = this.simulationRows();
    return rows.some((row) => this.isComplete(row)) && !rows.some((row) => this.hasInvalidNutrientId(row));
  });

  // Every edit replaces the row object, and PrimeNG's default row identity is the object itself, so without this
  // the table rebuilds the row on each change, which costs the inputs their focus between keystrokes.
  readonly trackSimulationRow = (_: number, row: SimulationRow): number => row.key;
  readonly trackDay = (_: number, day: DailyResultDay): string => day.date;

  readonly severity = dailyResultSeverity;
  readonly band = dailyResultBand;
  readonly utcStamp = utcStamp;

  constructor() {
    inject(DestroyRef).onDestroy(() => (this.destroyed = true));
  }

  reload(): void {
    this.daysResource.reload();
  }

  // A day with no consumption, or one that could not be scored, stores no categories or nutrients.
  hasBreakdown(result: DailyResultV1): boolean {
    return result.categories.length > 0 || result.nutrients.length > 0;
  }

  hasInvalidNutrientId(row: SimulationRow): boolean {
    const nutrientId = row.nutrientId.trim();
    return nutrientId.length > 0 && !NUTRIENT_ID.test(nutrientId);
  }

  onRescore(): void {
    const from = this.rescoreFrom();
    const to = this.rescoreTo();
    if (!from && !to) {
      this.confirmRescore(
        "Re-score the look-back window for this Youtopian? The engine reckons the window in each household's local days."
      );
      return;
    }

    if (!from || !to) {
      this.warn('Incomplete range', 'Set both From and To, or clear both to rescore the look-back window.');
      return;
    }

    const range = { from: toIsoDate(from), to: toIsoDate(to) };
    if (range.from > range.to) {
      this.warn('Invalid range', 'From must not be after To.');
      return;
    }

    if (inclusiveDays(from, to) > MAX_RESCORE_RANGE_DAYS) {
      this.warn('Range too long', `The range must be at most ${MAX_RESCORE_RANGE_DAYS} days.`);
      return;
    }

    this.confirmRescore(
      `Re-score every day from ${range.from} to ${range.to} for this Youtopian? The range is taken as given, so a day still open in the household's zone is scored as it stands.`,
      range
    );
  }

  addSimulationRow(): void {
    this.simulationRows.update((rows) => [...rows, emptyRow()]);
  }

  removeSimulationRow(key: number): void {
    this.simulationRows.update((rows) => rows.filter((row) => row.key !== key));
  }

  updateSimulationRow(key: number, patch: Partial<Omit<SimulationRow, 'key'>>): void {
    this.simulationRows.update((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  async simulate(): Promise<void> {
    if (!this.canSimulate()) return;

    const youtopianId = this.youtopianId();
    const quantities = this.simulationRows()
      .filter((row) => this.isComplete(row))
      .map((row) => ({ nutrientId: row.nutrientId.trim(), quantity: row.quantity as number }));

    this.simulating.set(true);
    this.simulationError.set(null);
    this.simulation.set(null);
    try {
      const result = await firstValueFrom(this.dailyResults.simulate(youtopianId, { quantities }));
      if (this.isViewing(youtopianId)) this.simulation.set(result);
    } catch (error) {
      if (this.isViewing(youtopianId)) {
        this.simulationError.set(badRequestReason(error) ?? 'The simulation failed.');
      }
    } finally {
      this.simulating.set(false);
    }
  }

  private confirmRescore(message: string, range?: DailyResultsRescoreRange): void {
    this.confirmationService.confirm({
      message: `${message} The sweep overwrites the stored results within seconds.`,
      header: 'Confirm Rescore',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.rescore(range)
    });
  }

  private async rescore(range?: DailyResultsRescoreRange): Promise<void> {
    const youtopianId = this.youtopianId();
    this.rescoring.set(true);
    try {
      const marked = await this.mark(youtopianId, range);
      if (marked === null || !this.isViewing(youtopianId)) return;

      const outcome: RescoreOutcome = {
        marked,
        range: range ?? null,
        progress: 'waiting',
        queuedDays: marked,
        failureReason: null
      };
      if (marked === 0) {
        this.lastRescore.set({ ...outcome, progress: 'scored', queuedDays: 0 });
        this.warn(
          'Nothing to rescore',
          "The engine marked no days, so nothing was queued. It marks none when it cannot resolve the Youtopian's household."
        );
        return;
      }

      this.lastRescore.set(outcome);
      await this.followSweep(youtopianId, outcome);
    } finally {
      this.rescoring.set(false);
    }
  }

  private async mark(youtopianId: string, range?: DailyResultsRescoreRange): Promise<number | null> {
    try {
      return (await firstValueFrom(this.dailyResults.rescore(youtopianId, range))).marked;
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Rescore failed',
        detail: refusalReason(error) ?? 'The engine did not accept the request.'
      });
      return null;
    }
  }

  private async followSweep(youtopianId: string, outcome: RescoreOutcome): Promise<void> {
    const deadline = Date.now() + SWEEP_TIMEOUT_MS;
    try {
      for (;;) {
        const queue = await firstValueFrom(this.dailyResults.queuedDays(youtopianId));
        if (!this.isViewing(youtopianId)) return;

        const drained = queue.pending.length === 0 && !queue.truncated;
        if (drained || Date.now() >= deadline) {
          this.finishSweep(outcome, queue);
          return;
        }

        this.lastRescore.set({ ...outcome, queuedDays: queue.pending.length });
        await firstValueFrom(timer(SWEEP_POLL_MS));
        if (!this.isViewing(youtopianId)) return;
      }
    } catch {
      if (!this.isViewing(youtopianId)) return;
      this.lastRescore.set({ ...outcome, progress: 'unknown', queuedDays: 0 });
      this.reload();
      this.warn('Rescore queued', 'The queue could not be read. Reload the list once the sweep has run.');
    }
  }

  private finishSweep(outcome: RescoreOutcome, queue: DailyResultsQueueState): void {
    this.reload();

    if (queue.pending.length > 0) {
      const failureReason = queue.pending.find((row) => row.lastFailureReason)?.lastFailureReason ?? null;
      this.lastRescore.set({ ...outcome, progress: 'queued', queuedDays: queue.pending.length, failureReason });
      this.warn(
        'Rescore still queued',
        failureReason ?? `${queue.pending.length} day(s) are still waiting for the sweep.`
      );
      return;
    }

    if (queue.truncated) {
      this.lastRescore.set({ ...outcome, progress: 'unknown', queuedDays: 0 });
      this.warn(
        'Rescore queued',
        'The queue is too long to tell whether these days were scored. Reload the list later.'
      );
      return;
    }

    this.lastRescore.set({ ...outcome, progress: 'scored', queuedDays: 0 });
    this.messageService.add({
      severity: 'success',
      summary: 'Rescore complete',
      detail: `${outcome.marked} day(s) scored. The list is current.`
    });
  }

  private isComplete(row: SimulationRow): boolean {
    return NUTRIENT_ID.test(row.nutrientId.trim()) && row.quantity !== null && row.quantity >= 0;
  }

  private isViewing(youtopianId: string): boolean {
    return !this.destroyed && this.youtopianId() === youtopianId;
  }

  private warn(summary: string, detail: string): void {
    this.messageService.add({ severity: 'warn', summary, detail });
  }
}
